import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  FooterActionBar,
  Icon,
  Illustration,
  type IllustrationKind,
  InlineMessage,
  InputField,
  Loader,
  ModalIsland,
  TabContent,
  TableSortable,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';

import { useAlertBanner } from '../../../../shared/app/contexts/AlertBannerContext';
import useDebounce from '../../../../shared/hooks/useDebounce';
import {
  ALERT_BANNER_TYPES,
  OWNERS_COUNT_LIMIT,
  TOKENS,
} from '../../constants';
import { useManagePageDispatch } from '../../contexts/ManagePageContext';
import { useAgentIdentity, useSaveOwners } from '../../hooks/useAgentManage';
import { useEffectAfterMount } from '../../hooks/useEffectAfterMount';
import {
  getEditOwnerSortConfig,
  getEditOwnerTabColConfig,
  getOwnerSortConfig,
  getOwnerTabColConfig,
  sortOwners,
} from '../../utils/utilManagepage';
import { type AgentMetadata, type AgentOwner } from '../ManagePageTable';

import {
  getEditOwnerRowData,
  getIngestionOwnerEditRow,
  getOwnerRowData,
  RenderIdentity,
} from './SidePanelShared';

interface SidePanelOwnersTabProps {
  ownersDetails: {
    agentId: string;
    owners: AgentOwner[];
  };
  activeTab: string;
  agentMetadata?: AgentMetadata | null;
}

/**
 * Owners tab for the agent side panel (tab value `"3"`).
 *
 * Displays a read-only sortable table of the agent's current owners with name/type popovers,
 * source chips, role chips, and rank. When no owners exist, shows an empty-state illustration
 * with an "Add Owners" CTA.
 *
 * An "Edit Owners" modal provides two views toggled by a {@link ToggleButtonGroup}:
 * - Assigned — editable table with role/rank dropdowns and remove buttons.
 *   Shows inline validation when business or technical owner counts exceed
 *   `OWNERS_COUNT_LIMIT / 2`.
 * - Available — searchable identity list (debounced) fetched via {@link useAgentIdentity},
 *   with add/remove toggles per identity. Capped at {@link OWNERS_COUNT_LIMIT} total owners.
 *
 * Saves are performed through {@link useSaveOwners}; success/error feedback is surfaced
 * via the shared alert banner from {@link useManagePage}.
 *
 * @param props.ownersDetails - Object containing `agentId` and the current `owners` array.
 * @param props.activeTab - Currently active tab value for {@link TabContent} visibility control.
 *
 * @returns Tab content with the owners table (or empty state) and the edit modal.
 */
export default function SidePanelOwnersTab({
  ownersDetails,
  activeTab,
  agentMetadata,
}: SidePanelOwnersTabProps) {
  // Hooks
  const intl = useIntl();

  // States
  const [ownerColumns, setOwnerColumns] = useState(getOwnerTabColConfig(intl));
  const [editOwnerColumns, setEditOwnerColumns] = useState(
    getEditOwnerTabColConfig(intl)
  );
  const [originalOwners, setOriginalOwners] = useState<AgentOwner[]>(
    ownersDetails.owners
  );
  const [currentOwners, setCurrentOwners] = useState<AgentOwner[]>([]);
  const [userPopover, setUserPopover] = useState<boolean[]>([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [ownerSearchText, setOwnerSearchText] = useState('');
  const [assignType, setAssignType] = useState(1);

  // Hooks
  const dispatch = useManagePageDispatch();
  const { show: showAlertBanner } = useAlertBanner();
  const debouncedOwnerSearch = useDebounce<string>(ownerSearchText, 300);
  const {
    data: agentIdentityList = [],
    isFetching,
    isLoading,
    isError,
  } = useAgentIdentity({
    searchText: debouncedOwnerSearch,
  });

  const { mutate: saveOwners, isPending } = useSaveOwners({
    onSuccess: (response) => {
      setIsEditModalOpen(false);
      setOriginalOwners(response);

      dispatch({
        refreshAgent: { [ownersDetails.agentId]: { owners: response } },
      });
      showAlertBanner({
        colorTheme: ALERT_BANNER_TYPES.SUCCESS,
        title: intl.formatMessage({
          id: 'yg2HFT',
          defaultMessage: 'Success',
          description: 'Success',
        }),
        description: intl.formatMessage({
          id: 'pXQqgG',
          defaultMessage: 'Owners have been updated successfully.',
          description: 'Owners have been updated successfully.',
        }),
      });
    },
    onError: (errorMessage) => {
      setIsEditModalOpen(false);

      showAlertBanner({
        colorTheme: ALERT_BANNER_TYPES.CRITICAL,
        title: intl.formatMessage({
          id: 'KFYqlI',
          defaultMessage: 'Failed',
          description: 'Failed',
        }),
        description: errorMessage,
      });
    },
  });

  // Constants
  const ingestionOwner: AgentOwner | null = useMemo(() => {
    const info = agentMetadata?.ownerInfo;

    if (!info?.ownerFullName || !info?.ownerEmail) {
      return null;
    }

    return {
      id: -1,
      name: info.ownerFullName.replace(/^#\s*/, ''),
      email: info.ownerEmail,
      role: 'Technical Owner',
      type: null,
      source: 'Ingestion',
      rank: 1,
    };
  }, [agentMetadata]);

  const displayOwners = useMemo(() => {
    if (!ingestionOwner) {
      return originalOwners;
    }

    return [ingestionOwner, ...originalOwners];
  }, [ingestionOwner, originalOwners]);

  const isNoOwner = useMemo(() => displayOwners.length === 0, [displayOwners]);
  const currentOwnerEmails = useMemo(
    () => new Set(currentOwners.map((co) => co.email)),
    [currentOwners]
  );
  const businessOwnersCount = useMemo(
    () =>
      currentOwners.filter((co) => co.role.toLowerCase().includes('business'))
        .length,
    [currentOwners]
  );
  const technicalOwnersCount = useMemo(
    () =>
      currentOwners.filter((co) => co.role.toLowerCase().includes('technical'))
        .length,
    [currentOwners]
  );
  const isSaveButtonEnabled = useMemo(() => {
    if (originalOwners.length !== currentOwners.length) {
      return (
        businessOwnersCount <= OWNERS_COUNT_LIMIT / 2 &&
        technicalOwnersCount <= OWNERS_COUNT_LIMIT / 2 &&
        businessOwnersCount + technicalOwnersCount === currentOwners.length
      );
    }

    const ownerMap = Object.fromEntries(
      originalOwners.map((od) => [
        String(od.id),
        { role: od.role, rank: od.rank },
      ])
    );
    const changeMap = currentOwners.map((co) => {
      if (
        String(co.id) in ownerMap &&
        ownerMap[String(co.id)].rank === co.rank &&
        ownerMap[String(co.id)].role === co.role
      ) {
        return false;
      }

      return true;
    });

    return (
      changeMap.some((cv) => cv) &&
      businessOwnersCount <= OWNERS_COUNT_LIMIT / 2 &&
      technicalOwnersCount <= OWNERS_COUNT_LIMIT / 2
    );
  }, [
    originalOwners,
    currentOwners,
    businessOwnersCount,
    technicalOwnersCount,
  ]);

  // Effects
  useEffect(() => {
    const popoverCount = ownersDetails.owners.length + (ingestionOwner ? 1 : 0);

    setOwnerSearchText('');
    setUserPopover(Array.from({ length: popoverCount }, () => false));
    setOriginalOwners(ownersDetails.owners);
    setCurrentOwners(ownersDetails.owners);
    setIsEditModalOpen(false);

    if (ownersDetails.owners.length === 0 && !ingestionOwner) {
      setAssignType(2);
    }
  }, [ownersDetails.agentId, ownersDetails.owners, ingestionOwner]);

  useEffectAfterMount(() => {
    if (currentOwners.length === 0 && !ingestionOwner) {
      setAssignType(2);
    }
  }, [currentOwners.length, ingestionOwner]);

  // Reset transient modal state on every close so the next open starts fresh.
  useEffect(() => {
    if (isEditModalOpen) {
      return;
    }

    setCurrentOwners(originalOwners);
    setOwnerSearchText('');
    setAssignType(originalOwners.length === 0 && !ingestionOwner ? 2 : 1);
  }, [isEditModalOpen, originalOwners, ingestionOwner]);

  const handleSort = useCallback(
    (columnKey: string, order: 'asc' | 'desc', isEditMode: boolean = false) => {
      if (isEditMode) {
        setCurrentOwners((prev) =>
          sortOwners(prev, { sortBy: columnKey, order })
        );

        return;
      }

      setOriginalOwners((prev) =>
        sortOwners(prev, { sortBy: columnKey, order })
      );
    },
    []
  );

  // Render
  const renderBody = () => {
    if (isNoOwner) {
      return (
        <div className='SidePanelOwnersTab-noOwnerContainer'>
          <Illustration kind={'Error' as IllustrationKind} />
          <Typography kind='h1' color={TOKENS.CF_NEU_BLD}>
            <FormattedMessage
              id='fB7088'
              defaultMessage='No Owners Assigned'
              description='No Owners Assigned'
            />
          </Typography>
          <Typography kind='body1' color={TOKENS.CF_NEU_MED}>
            <FormattedMessage
              id='6+CfSD'
              defaultMessage='Owners define governance and technical responsibility for this agent. Add at least one owner to establish accountability.'
              description='No owners description'
            />
          </Typography>
          <Button
            kind='filled'
            leftIcon={<Icon kind='Add' />}
            onClick={() => setIsEditModalOpen(true)}>
            <FormattedMessage
              id='dtxXJ/'
              defaultMessage='Add Owners'
              description='Add Owners'
            />
          </Button>
        </div>
      );
    }

    return (
      <>
        <div className='SidePanelOwnersTab-ownerTitleContainer'>
          <Typography kind='body2' color={TOKENS.CF_NEU_MED}>
            <FormattedMessage
              id='L3WeSn'
              defaultMessage='Owners define accountability for this agent. Every agent should have assigned owners to ensure governance oversight, operational continuity, and clear responsibility when changes occur.'
              description='Edit owners description'
            />
          </Typography>
          <Button
            kind='outlined'
            size='medium'
            leftIcon={<Icon kind='Add' />}
            onClick={() => setIsEditModalOpen(true)}>
            <FormattedMessage
              id='9fAuVS'
              defaultMessage='Edit Owners'
              description='Edit Owners'
            />
          </Button>
        </div>
        <div className='SidePanelOwnersTab-ownerTableContainer'>
          <TableSortable
            data={getOwnerRowData(
              displayOwners,
              userPopover,
              setUserPopover,
              intl
            )}
            columns={ownerColumns}
            setColumns={setOwnerColumns}
            sortConfigs={getOwnerSortConfig(intl)}
            columnConfigs={{ isDndDisabled: true }}
            isVirtualizationEnabled={false}
            onSort={handleSort}
            className='SidePanelOwnersTab-table'
          />
        </div>
      </>
    );
  };

  const renderOwnersList = () => {
    if (isFetching || isLoading) {
      return (
        <div className='SidePanelOwnersTab-intermediateStateWrapper'>
          <Loader kind='dots' format='inline' color='brand' />
        </div>
      );
    }

    if (isError) {
      return (
        <div className='SidePanelOwnersTab-intermediateStateWrapper'>
          <Icon kind='StopProcess' size='large' color={TOKENS.CF_CRT_MED} />
          <Typography kind='body2' color={TOKENS.CF_CRT_MED}>
            <FormattedMessage
              id='PoSV0I'
              defaultMessage="Data couldn't be retrieved."
              description='owner list error'
            />
          </Typography>
        </div>
      );
    }

    if (ownerSearchText.trim().length === 0 && agentIdentityList.length === 0) {
      return (
        <div className='SidePanelOwnersTab-intermediateStateWrapper'>
          <Icon kind='AlertInfo' size='large' color={TOKENS.CF_NEU_BLD} />
          <Typography kind='body2'>
            <FormattedMessage
              id='hJPsBy'
              defaultMessage='No Owners Available'
              description='owner no data'
            />
          </Typography>
        </div>
      );
    }

    if (agentIdentityList.length === 0) {
      return (
        <div className='SidePanelOwnersTab-intermediateStateWrapper'>
          <Illustration kind={'Empty' as IllustrationKind} />
          <Typography kind='body2'>
            <FormattedMessage
              id='GRmzuz'
              defaultMessage='No matching results found. Try clearing search or check back later.'
              description='no data after filter'
            />
          </Typography>
        </div>
      );
    }

    return agentIdentityList.map((usr) => {
      const isActive = currentOwnerEmails.has(usr.email);

      return (
        <RenderIdentity
          key={usr.id}
          name={usr.name}
          isActive={isActive}
          isDisabled={currentOwners.length >= OWNERS_COUNT_LIMIT && !isActive}
          type={usr.type}
          handleClick={() => {
            if (isActive) {
              setCurrentOwners((cusr) =>
                cusr.filter((u) => u.email !== usr.email)
              );

              return;
            }

            setCurrentOwners((cusr) => [
              ...cusr,
              {
                id: Number(usr.id),
                name: usr.name,
                email: usr.email,
                role:
                  businessOwnersCount < OWNERS_COUNT_LIMIT / 2
                    ? 'Business Owner'
                    : 'Technical Owner',
                type: usr.type,
                source: usr.type,
                rank: 1,
              },
            ]);
          }}
          intl={intl}
        />
      );
    });
  };

  return (
    <TabContent className='SidePanelOwnersTab' value={activeTab} tabValue='3'>
      <div className='SidePanelOwnersTab-ownerWrapper'>{renderBody()}</div>
      {isEditModalOpen && (
        <ModalIsland
          isOpen={isEditModalOpen}
          onClose={() => {
            if (!isPending) {
              setIsEditModalOpen(false);
            }
          }}
          title={intl.formatMessage({
            id: '9fAuVS',
            defaultMessage: 'Edit Owners',
            description: 'Edit Owners',
          })}
          subtitle={intl.formatMessage({
            id: 'S9CLLS',
            defaultMessage:
              'Assign owners to ensure clear accountability for this agent. Owners provide governance oversight and operational continuity when changes occur.',
            description:
              'Assign owners to ensure clear accountability for this agent. Owners provide governance oversight and operational continuity when changes occur.',
          })}
          headerIcon={
            <Icon kind='Edit' size='large' color={TOKENS.CF_NEU_BLD} />
          }
          className='SidePanelOwnersTab-editModal'
          contentBody={
            <div className='SidePanelOwnersTab-editModalContent'>
              <div className='SidePanelOwnersTab-editModalToggleWrapper'>
                <ToggleButtonGroup
                  selection={assignType}
                  setSelection={setAssignType}>
                  <ToggleButton
                    label={intl.formatMessage({
                      id: 'QoS40L',
                      defaultMessage: 'Assigned',
                      description: 'Assigned',
                    })}
                    size='medium'
                    isDisabled={currentOwners.length === 0 && !ingestionOwner}
                    BadgeComp={
                      <Badge
                        badgeStyle='outlined'
                        color='secondary'
                        kind='number'
                        size='medium'
                        number={currentOwners.length + (ingestionOwner ? 1 : 0)}
                      />
                    }
                  />
                  <ToggleButton
                    label={intl.formatMessage({
                      id: 'YXOeL5',
                      defaultMessage: 'Available',
                      description: 'Available',
                    })}
                    size='medium'
                  />
                </ToggleButtonGroup>
                {assignType === 1 && (
                  <Typography
                    kind='body2'
                    color={TOKENS.CF_NEU_MED}
                    isNoWrap
                    lineLimit={1}>
                    <FormattedMessage
                      id='pnDH/Z'
                      defaultMessage='You can add up to {limit} Business Owners and {limit} Technical Owners each.'
                      description='available subtitle'
                      values={{ limit: OWNERS_COUNT_LIMIT / 2 }}
                    />
                  </Typography>
                )}
                {assignType === 2 && (
                  <Typography
                    kind='body2'
                    color={TOKENS.CF_NEU_MED}
                    isNoWrap
                    lineLimit={1}>
                    <FormattedMessage
                      id='DUmWFO'
                      defaultMessage='Select users or groups to add as owners. Newly added owners will appear in Assigned. You can add up to {limit} ranked owners.'
                      description='available subtitle'
                      values={{ limit: OWNERS_COUNT_LIMIT }}
                    />
                  </Typography>
                )}
              </div>
              {assignType === 1 && (
                <>
                  {businessOwnersCount > OWNERS_COUNT_LIMIT / 2 && (
                    <InlineMessage
                      kind='background'
                      text={intl.formatMessage(
                        {
                          id: 'bHdmFm',
                          defaultMessage:
                            'Maximum {limit} users could be assigned Business Owner role',
                          description: 'max owner limit',
                        },
                        { limit: OWNERS_COUNT_LIMIT / 2 }
                      )}
                      colorTheme='critical'
                      size='medium'
                      isVisible
                      leftIcon={<Icon kind='AlertCriticalFilled' />}
                      className='SidePanelOwnersTab-roleCountMessage'
                    />
                  )}
                  {technicalOwnersCount > OWNERS_COUNT_LIMIT / 2 && (
                    <InlineMessage
                      kind='background'
                      text={intl.formatMessage(
                        {
                          id: 'kcwEWp',
                          defaultMessage:
                            'Maximum {limit} users could be assigned Technical Owner role',
                          description: 'max owner limit',
                        },
                        { limit: OWNERS_COUNT_LIMIT / 2 }
                      )}
                      colorTheme='critical'
                      size='medium'
                      isVisible
                      leftIcon={<Icon kind='AlertCriticalFilled' />}
                      className='SidePanelOwnersTab-roleCountMessage'
                    />
                  )}
                  <div className='SidePanelOwnersTab-ownersTableWrapper'>
                    <TableSortable
                      data={[
                        ...(ingestionOwner
                          ? [getIngestionOwnerEditRow(ingestionOwner)]
                          : []),
                        ...getEditOwnerRowData(
                          currentOwners,
                          setCurrentOwners,
                          intl
                        ),
                      ]}
                      columns={editOwnerColumns}
                      setColumns={setEditOwnerColumns}
                      sortConfigs={getEditOwnerSortConfig(intl)}
                      columnConfigs={{ isDndDisabled: true }}
                      isVirtualizationEnabled={false}
                      onSort={(columnKey: string, order: 'asc' | 'desc') =>
                        handleSort(columnKey, order, true)
                      }
                      className={cx(
                        'SidePanelOwnersTab-table',
                        'SidePanelOwnersTab-table--isEditMode'
                      )}
                    />
                  </div>
                </>
              )}
              {assignType === 2 && (
                <div className='SidePanelOwnersTab-ownersListWrapper'>
                  <InputField
                    name='basic'
                    placeholder={intl.formatMessage({
                      id: '0s6+Me',
                      defaultMessage:
                        'Search by user name, email, or user group',
                      description: 'Search by user name, email, or user group',
                    })}
                    kind='search'
                    prefixIcon={
                      <Icon kind='Search' color={TOKENS.CF_NEU_STL} />
                    }
                    value={ownerSearchText}
                    setValue={setOwnerSearchText}
                  />
                  <div className='SidePanelOwnersTab-ownersListContainer'>
                    {renderOwnersList()}
                  </div>
                </div>
              )}
            </div>
          }
          FooterActionBarComp={
            <FooterActionBar
              size='large'
              className='SidePanelOwnersTab-editModalFooter'>
              <div />
              <div className='SidePanelOwnersTab-editModalFooterWrapper'>
                <Button
                  kind='subtle'
                  size='medium'
                  isDisabled={isPending}
                  onClick={() => setIsEditModalOpen(false)}>
                  <FormattedMessage
                    id='js69XA'
                    defaultMessage='Cancel'
                    description='cancel'
                  />
                </Button>
                <div className='SidePanelOwnersTab-editModalFooterSaveBtn'>
                  <Tooltip
                    isDisabled={isPending || isSaveButtonEnabled}
                    trigger={
                      <Button
                        kind={isSaveButtonEnabled ? 'filled' : 'outlined'}
                        size='medium'
                        isDisabled={!isSaveButtonEnabled || isPending}
                        isLoading={isPending}
                        loaderText={intl.formatMessage({
                          id: '1yxebr',
                          defaultMessage: 'Saving...',
                          description: 'Saving...',
                        })}
                        onClick={() =>
                          saveOwners({
                            agentId: ownersDetails.agentId,
                            owners: currentOwners,
                          })
                        }>
                        <FormattedMessage
                          id='kY2alH'
                          defaultMessage='Save'
                          description='save'
                        />
                      </Button>
                    }
                    text={intl.formatMessage({
                      id: 'semxkT',
                      defaultMessage:
                        'We found some issues with your input. Please update the fields and try again.',
                      description:
                        'We found some issues with your input. Please update the fields and try again.',
                    })}
                    isPortalEnabled
                    placement='top'
                  />
                </div>
              </div>
            </FooterActionBar>
          }
          classNames={{
            bodyClassName: 'SidePanelOwnersTab-editModalBody',
          }}
        />
      )}
    </TabContent>
  );
}
